/**
 * GTD Domain Integration Module
 * 
 * This module provides the integration interface for GTD Domain
 * following Eric Evans Strategic DDD patterns. It exposes the
 * Published Language contracts that downstream contexts can depend on.
 * 
 * Integration Patterns:
 * - Published Language: Stable contracts for downstream contexts
 * - Customer/Supplier: GTD is upstream, consumers are downstream
 * - Anti-Corruption Layer: Consumers should translate to their own models
 */

// Export Published Language contracts
export * from './contracts';

export const publishedLanguage = {
  /**
   * Domain information - Runtime metadata
   */
  domain: {
    name: 'GTD Domain',
    version: '1.0.0',
    description: 'Getting Things Done methodology implementation'
  },

  /**
   * Integration Capabilities - What this domain provides to consumers
   */
  capabilities: {
    // Inbox Management
    'capture-item': 'Capture thoughts, ideas, and tasks into trusted system',
    'list-inbox-items': 'Retrieve all unprocessed inbox items',
    'clarify-item': 'Process inbox item through GTD clarification workflow',
    
    // Project Management  
    'create-project': 'Create new projects with defined outcomes',
    'list-projects': 'Get all active projects and their status',
    'get-project-actions': 'Retrieve next actions for specific project',
    
    // Action Management
    'create-next-action': 'Create actionable tasks with context and energy level',
    'list-actions-by-context': 'Get actions filtered by GTD context (@calls, @computer, etc.)',
    'complete-action': 'Mark action as completed',
    
    // Review System
    'weekly-review': 'Comprehensive GTD weekly review process',
    'daily-review': 'Quick daily action planning',
    'get-system-statistics': 'Overall GTD system health metrics'
  },

  /**
   * Domain Events - Events published for downstream consumption
   */
  events: {
    // Inbox Events
    'item-captured': 'New item added to inbox for processing',
    'item-clarified': 'Inbox item processed through clarification workflow',
    'item-organized': 'Clarified item organized into appropriate lists',
    
    // Project Events
    'project-created': 'New project with defined outcome established',
    'project-completed': 'Project successfully completed',
    'project-archived': 'Project moved to archive',
    
    // Action Events
    'action-created': 'New next action defined',
    'action-completed': 'Action marked as done',
    'action-deferred': 'Action moved to someday/maybe list',
    'context-action-batch-completed': 'All actions in context completed',
    
    // Review Events
    'weekly-review-completed': 'Weekly review process finished',
    'daily-review-completed': 'Daily planning session completed',
    'system-maintenance-performed': 'GTD system cleanup and organization'
  },

  /**
   * Business Rules - Key constraints and policies
   */
  businessRules: {
    'two-minute-rule': 'Tasks taking less than 2 minutes should be done immediately',
    'single-source-truth': 'Every commitment must be captured in trusted system',
    'next-action-clarity': 'Every project must have at least one defined next action',
    'context-based-organization': 'Actions organized by context (@calls, @computer, etc.)',
    'weekly-review-required': 'Complete weekly review to maintain system integrity',
    'inbox-zero-goal': 'Process inbox to empty during each review cycle'
  },

  /**
   * Integration Patterns - Recommended integration approaches
   */
  integrationPatterns: {
    'capture-ubiquitous': 'Provide multiple capture methods for universal collection',
    'clarify-systematic': 'Use consistent clarification workflow for all items',
    'organize-contextual': 'Organize by context and energy level for optimal execution',
    'review-regular': 'Implement regular review cycles for system maintenance',
    'engage-intuitive': 'Enable intuitive action selection based on context and energy'
  }
};

/**
 * Integration metadata for downstream contexts
 */
export const GTD_INTEGRATION_INFO = {
  version: '1.0.0',
  context: 'GTD Domain',
  role: 'Upstream Supplier',
  relationships: [
    'AI Domain (Customer)',
    'Builder MCP (Customer)',
    'Designer MCP (Customer)'
  ],
  publishedLanguage: {
    contracts: [
      'InboxQueryCriteria',
      'InboxItemSummary', 
      'InboxCaptureRequest',
      'InboxCaptureResult',
      'InboxStatistics'
    ],
    stability: 'Stable - Breaking changes will increment major version'
  }
} as const;